import { definedMap } from "@glideapps/ts-necessities";
import { withDefault } from "collection-utils";
import * as iots from "io-ts";
import { baseThemeCodec } from "../BaseTheme";
import { tableNameCodec } from "../database-strings";
import type { BuilderAction } from "../Database/builder-actions";
import type { TabAppearance, TabDescription, TableRefGlideType } from "../description";
import { screenDescriptionCodec, tabDescriptionCodec, userProfileDescriptionCodec } from "../description";
import type { SourceMetadata } from "../source-metadata";
import { getGoogleSheetsSourceMetadata, sourceMetadataCodec } from "../source-metadata";
import { assert, assertNever, defined, definedMapWithDefault, deleteUndefined } from "../Support";
import { AppKind, appKindCodec } from "./app-kind";
import type { IconImage } from "./icon-image";
import { iconImageCodec } from "./icon-image";

export { AppKind } from "./app-kind";
export type { IconImage } from "./icon-image";

export interface SerializedComponent {
    readonly $kind: string;
    readonly [arg: string]: SerializedComponent | any;
}

export interface SerializedMenuItem {
    readonly title: SerializedComponent;
    readonly icon: SerializedComponent;
    readonly onTap: SerializedComponent;
    readonly isEnabled?: SerializedComponent;
    readonly isVisible?: SerializedComponent;
    readonly href?: SerializedComponent;
    readonly style?: SerializedComponent;
    readonly purpose?: SerializedComponent;
}

export interface SerializedPivot {
    readonly componentName: string;
    readonly menuItems: ReadonlyArray<SerializedMenuItem>;
}

export enum ScreenFlag {
    IsEditScreen = "is-edit-screen",
    IsAddItemScreen = "is-add-item-screen",
    IsFormScreen = "is-form-screen",
    IsTopScreen = "is-top-screen",
    IsArrayScreen = "is-array-screen",
    IsMapScreen = "is-map-screen",
    IsUserProfileScreen = "is-user-profile-screen",
    IsUnconfigurable = "is-unconfigurable",
    IsSignaturePad = "is-signature-pad",
    IsShareScreen = "is-share-screen",
    IsFinalTransient = "is-final-transient",
    IsErrorScreen = "is-error-screen",
}

// An instance of this must have either `pivots` or `componentName`.
export interface SerializedScreen {
    readonly pivots?: ReadonlyArray<SerializedPivot>;
    readonly ctxModifier?: SerializedComponent;

    readonly title?: SerializedComponent;
    readonly topBar?: SerializedComponent;
    readonly pivotPicker?: SerializedComponent;

    readonly quotaBannerMessage?: SerializedComponent;

    readonly flags?: ReadonlyArray<ScreenFlag>;

    // legacy, from before we had pivots
    readonly componentName?: string;
    readonly menuItems?: ReadonlyArray<SerializedMenuItem>;
}

export interface SerializedRootScreen extends SerializedScreen {
    readonly title?: SerializedComponent;
    readonly ctxFromRootContext?: SerializedComponent;
    readonly isVisible?: SerializedComponent;
}

export interface SerializedRoot {
    readonly tabs: ReadonlyArray<SerializedRootScreen>;
    readonly ctx: SerializedComponent;
}

// FIXME: This is a bit stupid.  `SerializedRoot.tabs` should only
// contain the non-hidden tabs, and they should probably have an index
// into the `AppDescription.tabs` array so we can relate them.
export function firstNonHiddenNonFlyoutTabIndex(tabs: ReadonlyArray<TabAppearance> | undefined): number {
    return definedMapWithDefault(tabs, 0, t =>
        defined(
            Math.max(
                0,
                t.findIndex(td => !td.hidden && td.inFlyout !== true)
            )
        )
    );
}

export enum AppAuthenticationKind {
    Disabled = "disabled",
    Password = "password",
    EmailPin = "email-pin",
}

// This means don't allow authentication.  We use this in Pages.
const disabledAppAuthentication = iots.type({
    kind: iots.literal(AppAuthenticationKind.Disabled),
});

const passwordAppAuthentication = iots.intersection([
    iots.type({
        password: iots.string,
    }),
    iots.partial({
        kind: iots.literal(AppAuthenticationKind.Password),
    }),
]);
type PasswordAppAuthentication = iots.TypeOf<typeof passwordAppAuthentication>;

export enum AuthenticationEmailSource {
    Table = "table",
    OrgMembers = "org-members",
    JustMe = "just-me",
    UserProfiles = "user-profiles",
}

const emailWithPin = iots.type({
    email: iots.string,
    pin: iots.string,
});
export type EmailWithPin = iots.TypeOf<typeof emailWithPin>;

const emailPinAppAuthentication = iots.intersection([
    iots.type({
        kind: iots.literal(AppAuthenticationKind.EmailPin),
    }),
    iots.partial({
        // Default is `Table`.  In that case, if `emailTableName` is missing, the app is public.
        // For `OrgMembers` the `emailTableName` is ignored.
        source: iots.union([
            iots.literal(AuthenticationEmailSource.Table),
            iots.literal(AuthenticationEmailSource.OrgMembers),
            iots.literal(AuthenticationEmailSource.JustMe),
            iots.literal(AuthenticationEmailSource.UserProfiles),
        ]),
        emailTableName: iots.union([iots.string, tableNameCodec]),
        // This is valid only if there's an `emailTableName`.
        additionalEmailsWithPins: iots.readonlyArray(emailWithPin),
        optional: iots.boolean, // defaults to `false`, currently only used in Pages
    }),
]);
export type EmailPinAppAuthentication = iots.TypeOf<typeof emailPinAppAuthentication>;

export const appAuthenticationCodec = iots.union([
    disabledAppAuthentication,
    passwordAppAuthentication,
    emailPinAppAuthentication,
]);
export type AppAuthentication = iots.TypeOf<typeof appAuthenticationCodec>;

export function isPrivateAuthentication(authentication: AppAuthentication): boolean {
    if (authentication.kind === AppAuthenticationKind.Password) return true;
    if (authentication.kind === AppAuthenticationKind.EmailPin) {
        if (
            authentication.source !== undefined &&
            (authentication.source === AuthenticationEmailSource.JustMe ||
                authentication.source === AuthenticationEmailSource.OrgMembers ||
                authentication.source === AuthenticationEmailSource.UserProfiles ||
                (authentication.source === AuthenticationEmailSource.Table &&
                    authentication.emailTableName !== undefined))
        )
            return true;
    }
    return false;
}

export function getAppAuthenticationKind({ kind }: AppAuthentication): AppAuthenticationKind {
    return kind ?? AppAuthenticationKind.Password;
}

export function isPasswordAppAuthentication(
    appAuthentication: AppAuthentication
): appAuthentication is PasswordAppAuthentication {
    return getAppAuthenticationKind(appAuthentication) === AppAuthenticationKind.Password;
}

export function getAuthenticationEmailSource(auth: EmailPinAppAuthentication): AuthenticationEmailSource {
    return auth.source ?? AuthenticationEmailSource.Table;
}

export function detailsForAppAuthentication(authentication: AppAuthentication | undefined): string {
    if (authentication === undefined) return "Your app is public";

    switch (authentication.kind) {
        case AppAuthenticationKind.Disabled:
            return "Your app does not allow signing in";
        case AppAuthenticationKind.Password:
            return "Your app is password protected";
        case AppAuthenticationKind.EmailPin:
            if (authentication.source === AuthenticationEmailSource.Table) {
                if (authentication.emailTableName === undefined) {
                    return "Public with email";
                } else {
                    return "Access limited by email";
                }
            } else if (authentication.source === AuthenticationEmailSource.JustMe) {
                return "Only you can access your app";
            } else if (authentication.source === AuthenticationEmailSource.OrgMembers) {
                return "Team members only";
            } else if (authentication.source === AuthenticationEmailSource.UserProfiles) {
                return "Only users with a profile can access your app";
            } else if (authentication.source === undefined) {
                return "Email Pin";
            } else {
                return assertNever(authentication.source);
            }
        case undefined:
            return "Your app is public";
        default:
            return assertNever(authentication);
    }
}

export enum EmailClient {
    NativeMail = "native-mail",
    Gmail = "gmail",
    MSOutlook = "ms-outlook",
}

export function isAppKindSupported(appKind: AppKind, supported: AppKind | "both"): boolean {
    return supported === "both" || appKind === supported;
}

const userSettableAppFeatures = iots.partial({
    emailClient: iots.union([
        iots.literal(EmailClient.NativeMail),
        iots.literal(EmailClient.Gmail),
        iots.literal(EmailClient.MSOutlook),
    ]),
    requirePinForComments: iots.boolean,
    isWorldCopyable: iots.boolean,
    // undefined or <1 means don't refresh automatically
    automaticRefreshMinutes: iots.number,
    // Due to the location of the Google Analytics configuration,
    // we have to store it somewhere that the builder can always edit it.
    // Since the app description is always around when that side panel is displayed,
    // here's a good place to drop it.
    //
    // Since both the availability and the values are in the same features type,
    // here's what's up: You can always _have_ a measure ID, but you can't always
    // _use_ it. This makes it possible to downgrade your app, keep your measure ID,
    // then upgrade it again, and watch everything just start working again.
    googleAnalyticsMeasureIDs: iots.array(iots.string),
    showSignInWithGoogle: iots.boolean,
    useCustomSignInBackgroundImageInLoading: iots.boolean,
    enableShareScreen: iots.boolean,
    shareScreenTitle: iots.string,
    disableSharing: iots.boolean,
    defaultTabletMode: iots.boolean,
    virtualEmailAddresses: iots.boolean,
    askUserToSaveAuthCookie: iots.boolean,
    appKind: appKindCodec,
    useNCM: iots.boolean,
});
export type UserSettableAppFeatures = iots.TypeOf<typeof userSettableAppFeatures>;

export enum TemplateProvenance {
    None = "none",
    Free = "free",
    Paid = "paid",
}

export function doesTemplateProvenanceAllowCopying(provenance: TemplateProvenance | undefined): boolean {
    return (provenance ?? TemplateProvenance.None) !== TemplateProvenance.Paid;
}

export function doesTemplateProvenanceAllowSubmitting(provenance: TemplateProvenance | undefined): boolean {
    return provenance === undefined || provenance === TemplateProvenance.None || provenance === TemplateProvenance.Free;
}

const persistentAppFeatures = iots.partial({
    templateProvenance: iots.union([
        iots.literal(TemplateProvenance.None),
        iots.literal(TemplateProvenance.Free),
        iots.literal(TemplateProvenance.Paid),
    ]),
});
export type PersistentAppFeatures = iots.TypeOf<typeof persistentAppFeatures>;

const nonUserAppFeatures = iots.intersection([
    userSettableAppFeatures,
    iots.partial({
        // FIXME: remove this - everything we need to know is in the eminence
        // flags
        noRowLimit: iots.boolean,
        allowTabletMode: iots.boolean,
        isTrialLocked: iots.boolean,
    }),
    persistentAppFeatures,
]);
export type NonUserAppFeatures = iots.TypeOf<typeof nonUserAppFeatures>;

const baseAppLoginDataCodec = iots.intersection([
    iots.type({
        title: iots.string,
        theme: baseThemeCodec,
    }),
    iots.partial({
        description: iots.string,
        author: iots.string,
        iconImage: iconImageCodec,
        features: nonUserAppFeatures,
    }),
]);
export type BaseAppLoginData = iots.TypeOf<typeof baseAppLoginDataCodec>;

export const appDescriptionCodec = iots.intersection([
    baseAppLoginDataCodec,
    iots.type({
        screenDescriptions: iots.record(iots.string, screenDescriptionCodec),
    }),
    iots.partial({
        // Legacy
        // Do not access these directly; use getSourceMetadata instead.
        sourceMetadata: sourceMetadataCodec,
        sourceMetadataArray: iots.readonlyArray(sourceMetadataCodec),
        tabs: iots.readonlyArray(tabDescriptionCodec),
        authentication: appAuthenticationCodec,
        userProfile: userProfileDescriptionCodec,
    }),
]);
export type AppDescription = iots.TypeOf<typeof appDescriptionCodec>;

export function getAppTabs(app: AppDescription): readonly TabDescription[] {
    return withDefault(app.tabs, []);
}

export function getAppIconImage(app: AppDescription): IconImage {
    if (app.iconImage !== undefined) {
        return app.iconImage;
    }
    return { emoji: "😱" };
}

export const defaultAppDescriptionText = "";
export function isDefaultAppDescriptionText(desc: string): boolean {
    return desc.trim().length === 0;
}

export function getSourceMetadata(appDescription: AppDescription): readonly SourceMetadata[] {
    if (appDescription.sourceMetadataArray !== undefined) {
        return appDescription.sourceMetadataArray;
    }
    assert(appDescription.sourceMetadata !== undefined);
    return [appDescription.sourceMetadata];
}

export function sheetFileIDForAppDescription(appDescription: AppDescription): string | undefined {
    return getGoogleSheetsSourceMetadata(getSourceMetadata(appDescription))?.id;
}

export function isReloadable(appDescription: AppDescription): boolean {
    return (
        sheetFileIDForAppDescription(appDescription) !== undefined ||
        getSourceMetadata(appDescription).some(i => i.type === "Native table" && i.externalSource !== undefined)
    );
}

export interface AppManifest {
    readonly short_name?: string;
    readonly name: string;
    readonly description?: string;
    readonly author?: string;
    readonly icons: ReadonlyArray<AppManifestIcon>;
    readonly start_url: string;
    readonly background_color: string;
    readonly display: string;
    readonly scope?: string;
    readonly theme_color: string;
    glidePWAAddToHead?: string;
}

export interface AppManifestIcon {
    readonly src: string;
    readonly type: string;
    readonly sizes: string;
}

export const defaultUserFeatures = {
    removeBranding: false,
    primaryKeyProperties: false,
    addRowToSheet: false,
    emailClientOption: false,
    omitNotifications: false,
    copyToClipboardAction: false,
    advancedPro: false,
    adminPanel: false,
    appStoreReview: false,
    // This property will allow the user to ignore app eminence and bypass checks
    // for if an app is submittable as a template. If it is set to true, this will allow
    // apps copied from a template to be resubmitted as well as allowing org members to
    // submit any of the org's apps as templates
    forceAllowTemplateSubmit: false,
    autoApproveTemplate: false,
    // Promo codes are typically only applicable to new customers.
    // Existing non-customer users are considered new customers.
    canApplyPromoCodes: false,
    alternateUniverseGlide: false,
    previewLiveLinearBarcodeScanner: false,
    massScanBarcodes: false,
    unlimitedBarcodeScanning: false,
    offlineActionQueue: false,
    ncmPerformanceAnalysis: false,
    glidePages: false,
    allowExcelSource: false,
    allowAirtableSource: false,
    dbrScannerHandshake: undefined as string | undefined,
    nonBlockingButton: false,
    // If this is "true" then the user cannot even opt in to tracking,
    // it's just disabled. We primarily use this for Enterprise accounts.
    builderTrackingDisableAnalytics: false,
    billingVNoneCodePaths: false,
};

export type UserFeatures = Readonly<typeof defaultUserFeatures>;

export const defaultNewUserFeatures = {
    ...defaultUserFeatures,
    canApplyPromoCodes: true,
};

type OrUndefined<T> = {
    [P in keyof T]: T[P] | undefined;
};

type RequiredNonUserAppFeatures = Required<NonUserAppFeatures>;
export function isolateNonUserAppFeatures(features: NonUserAppFeatures): OrUndefined<RequiredNonUserAppFeatures> {
    return {
        emailClient: features.emailClient,
        requirePinForComments: features.requirePinForComments,
        isWorldCopyable: features.isWorldCopyable,
        automaticRefreshMinutes: features.automaticRefreshMinutes,
        noRowLimit: features.noRowLimit,
        templateProvenance: features.templateProvenance,
        googleAnalyticsMeasureIDs: features.googleAnalyticsMeasureIDs,
        showSignInWithGoogle: features.showSignInWithGoogle,
        allowTabletMode: features.allowTabletMode,
        disableSharing: features.disableSharing,
        enableShareScreen: features.enableShareScreen,
        shareScreenTitle: features.shareScreenTitle,
        defaultTabletMode: features.defaultTabletMode,
        useCustomSignInBackgroundImageInLoading: features.useCustomSignInBackgroundImageInLoading,
        virtualEmailAddresses: features.virtualEmailAddresses,
        askUserToSaveAuthCookie: features.askUserToSaveAuthCookie,
        isTrialLocked: features.isTrialLocked,
        appKind: features.appKind,
        useNCM: features.useNCM,
    };
}

export function isolateUserSettableAppFeatures(
    features: UserSettableAppFeatures
): OrUndefined<Required<UserSettableAppFeatures>> {
    return {
        emailClient: features.emailClient,
        requirePinForComments: features.requirePinForComments,
        isWorldCopyable: features.isWorldCopyable,
        automaticRefreshMinutes: features.automaticRefreshMinutes,
        googleAnalyticsMeasureIDs: features.googleAnalyticsMeasureIDs,
        showSignInWithGoogle: features.showSignInWithGoogle,
        useCustomSignInBackgroundImageInLoading: features.useCustomSignInBackgroundImageInLoading,
        enableShareScreen: features.enableShareScreen,
        shareScreenTitle: features.shareScreenTitle,
        disableSharing: features.disableSharing,
        defaultTabletMode: features.defaultTabletMode,
        virtualEmailAddresses: features.virtualEmailAddresses,
        askUserToSaveAuthCookie: features.askUserToSaveAuthCookie,
        appKind: features.appKind,
        useNCM: features.useNCM,
    };
}

export function isolatePersistentAppFeatures(features: PersistentAppFeatures): PersistentAppFeatures {
    return {
        templateProvenance: features.templateProvenance,
    };
}

export interface AppFeatures extends UserFeatures, NonUserAppFeatures {}

// NOTE: We're assuming that all the grandfathering features are boolean
// in `makeAppFeatures`.
export const defaultNonUserAppFeatures: Required<NonUserAppFeatures> = {
    emailClient: EmailClient.NativeMail,
    requirePinForComments: true,
    isWorldCopyable: false,
    automaticRefreshMinutes: 0,
    noRowLimit: false,
    templateProvenance: TemplateProvenance.None,
    googleAnalyticsMeasureIDs: [],
    showSignInWithGoogle: false,
    allowTabletMode: false,
    useCustomSignInBackgroundImageInLoading: false,
    enableShareScreen: false,
    disableSharing: false,
    shareScreenTitle: "",
    defaultTabletMode: false,
    virtualEmailAddresses: false,
    askUserToSaveAuthCookie: false,
    isTrialLocked: false,
    appKind: AppKind.App,
    useNCM: false,
};

export const newAppNonUserAppFeatures = {
    ...defaultNonUserAppFeatures,
    virtualEmailAddresses: true,
};

export const defaultAppFeatures = {
    ...defaultUserFeatures,
    ...defaultNonUserAppFeatures,
};

export function getEmailClient(appFeatures: UserSettableAppFeatures): EmailClient {
    return withDefault(appFeatures.emailClient, defaultAppFeatures.emailClient);
}

export type SerializedComponentsObject = Record<string, SerializedComponent>;

export interface SerializedComputedColumn {
    readonly table: TableRefGlideType;
    readonly columnName: string;
    readonly value: SerializedComponent;
}

export interface SerializedColumnFormat {
    readonly table: TableRefGlideType;
    readonly columnName: string;
    readonly defaultFormat: SerializedComponent;
}

interface BaseSerializedAppLoginData extends BaseAppLoginData {
    readonly manifest?: AppManifest;
    readonly shortName?: string;
    readonly customDomain?: string;
    readonly features: AppFeatures | undefined;
}

export interface SerializedApp extends AppDescription, BaseSerializedAppLoginData {
    readonly root: SerializedRoot;
    readonly screens: Record<string, SerializedScreen>;
    // This used to be required, and is now set to `undefined`.  It's a
    // poison pill to force old Glide frontends to reload:
    // https://github.com/quicktype/glide/issues/3448
    readonly components?: SerializedComponentsObject;
    readonly functions: SerializedComponentsObject | undefined;
    readonly computedColumns: readonly SerializedComputedColumn[] | undefined;
    readonly features: AppFeatures | undefined;

    // We're only using this in wire apps.
    readonly builderActions: Record<string, BuilderAction> | undefined;
}

// ##appLoginData:
// If we can we should unify this with `PublishedApp`.
export interface AppLoginData extends BaseSerializedAppLoginData {
    readonly manifest: AppManifest;
    readonly features: AppFeatures;
    readonly iconImage: IconImage;
    readonly lastPasswordID?: string;
}

export function getAppFeatures(app: SerializedApp): AppFeatures;
export function getAppFeatures(app: AppDescription): NonUserAppFeatures;
export function getAppFeatures(app: AppDescription | SerializedApp): NonUserAppFeatures | AppFeatures {
    return withDefault(app.features, defaultAppFeatures);
}

export const defaultAppKind: AppKind = AppKind.App;

export function getAppKindFromFeatures(features: NonUserAppFeatures | undefined): AppKind {
    return features?.appKind ?? defaultAppKind;
}

export function getAppKind(app: AppDescription | undefined): AppKind {
    return getAppKindFromFeatures(definedMap(app, getAppFeatures));
}

export function isolateAppLoginData(desc: SerializedApp & { readonly manifest: AppManifest }): AppLoginData {
    return {
        title: desc.title,
        theme: desc.theme,
        description: desc.description,
        author: desc.author,
        iconImage: getAppIconImage(desc),
        features: deleteUndefined(withDefault(desc.features, defaultAppFeatures)),
        manifest: deleteUndefined(desc.manifest),
    };
}

export function makeSourceMetadataForAppDescription(sourceMetadataArray: readonly SourceMetadata[]): {
    readonly sourceMetadata: SourceMetadata | undefined;
    readonly sourceMetadataArray: readonly SourceMetadata[];
} {
    return {
        sourceMetadata: getGoogleSheetsSourceMetadata(sourceMetadataArray),
        sourceMetadataArray,
    };
}

export function isolateAppDescription(
    desc: AppDescription,
    sourceMetadata?: readonly SourceMetadata[]
): AppDescription {
    let isolated = {
        title: desc.title,
        description: desc.description,
        author: desc.author,
        sourceMetadata: desc.sourceMetadata,
        sourceMetadataArray: getSourceMetadata(desc),
        iconImage: desc.iconImage,
        tabs: desc.tabs,
        screenDescriptions: desc.screenDescriptions,
        theme: desc.theme,
        authentication: desc.authentication,
        features: desc.features,
        userProfile: desc.userProfile,
    };
    if (sourceMetadata !== undefined) {
        isolated = { ...isolated, ...makeSourceMetadataForAppDescription(sourceMetadata) };
    }
    return isolated;
}
